
import { Vue, Component } from 'vue-property-decorator';
import User from '@/models/User';
import { IformCreateOption } from 'ant-design-vue/types/form/form';
import { LoginValues } from '@/interfaces/components/LoginValues';
import { RouteNames } from '@/enums/routes/RouteNames';
import { TAnyKeyValueObject } from 'jsona/lib/JsonaTypes';
import { Col } from 'ant-design-vue';
import { Row } from 'ant-design-vue';
import { ErrorOptions } from '@/interfaces/ErrorOptions';
import NextStep from '@/models/NextStep';
import { optionalNextStepTypes } from '@/regex/nextStepRegex';
import moment from 'moment';
import { getValidationQuota } from '@/modules/Client/helpers/AddressValidationQuotaHelper';
import { useGlobalNotification } from '@/shared/composables/useGlobalNotification';

interface DynamicObject {
    [key: string]: any;
}

@Component({
    name: 'LoginForm',
    components: {
        Col,
        Row,
    },
})
export default class LoginForm extends Vue {
    private httpRequestStarted: boolean = false;
    private requiredFieldNames = ['username', 'password'];
    private routeNames = RouteNames;
    private initializeGlobalNotificationState = useGlobalNotification().initializeGlobalNotificationState;

    private get form() {
        return this.$form.createForm(this, { name: 'horizontal_login' } as IformCreateOption);
    }

    private mounted() {
        this.setDefaultLanguage();
    }

    private setDefaultLanguage() {
        const cacheLang = localStorage.getItem('languageLocale');

        if (cacheLang) {
            this.$i18n.locale = cacheLang;
        } else {
            const browserLang = window.navigator.language;
            const locale = browserLang.length === 2 ? browserLang : browserLang.slice(0, browserLang.indexOf('-'));
            this.$i18n.locale = locale;
        }
    }

    private async login(loginValues: LoginValues) {
        this.httpRequestStarted = true;
        const resetErrors: DynamicObject = {};
        this.requiredFieldNames.forEach((fieldName: string) => {
            resetErrors[fieldName] = {};
            resetErrors[fieldName].value = loginValues[fieldName] ? loginValues[fieldName] : '';
            resetErrors[fieldName].errors = [];
        });
        // resets errors before submitting the form
        this.form.setFields(resetErrors);
        try {
            await User.login(loginValues);
            this.showHolidayNotification();
            getValidationQuota();
            this.initializeGlobalNotificationState();
        } catch (e) {
            const errors: DynamicObject = {};
            const errorObj = e as ErrorOptions;

            if (errorObj.response?.data.errors) {
                errorObj.response.data.errors.forEach((errorDetails: TAnyKeyValueObject) => {
                    const field = errorDetails.source.pointer.split('/')[3];
                    if (errors[field] === undefined) {
                        errors[field] = {};
                        errors[field].value = loginValues[field] ? loginValues[field] : '';
                        errors[field].errors = [];
                    }
                    errors[field].errors.push(errorDetails.detail);
                });
            } else {
                // Handle general error case when structured error data is not available
                errors.username = {
                    value: loginValues.username || '',
                    errors: [this.$t('Invalid username or password')],
                };
                errors.password = {
                    value: loginValues.password || '',
                    errors: [],
                };
            }
            this.form.setFields(errors);
            this.httpRequestStarted = false;
            return;
        }

        // Clear cache storage before redirecting
        sessionStorage.clear();

        const redirectTo = this.$route.query.redirectTo;

        if (redirectTo) {
            this.$router.push({ path: redirectTo as string });
        } else {
            this.$router.push({ name: RouteNames.statisticsDashboard });
        }

        this.httpRequestStarted = false;
    }

    private get workFreeDays() {
        return this.$store.getters['tableData/workFreeDays'];
    }

    private async showHolidayNotification() {
        try {
            await NextStep.getWorkFreeDays();
            const currentDate = moment().startOf('day');

            // Filter for "Kolektivni dopust" activities
            const collectiveHolidays = this.workFreeDays.filter((day: any) => {
                const typeName = day.type?.name;
                return optionalNextStepTypes.includes(typeName) && typeName === 'Kolektivni dopust';
            });

            // Find the first holiday that includes today's date
            const todaysHoliday = collectiveHolidays.find((holiday: any) => {
                const dateFrom = moment(holiday.dateFrom).startOf('day');
                const dateTo = moment(holiday.dateTo).endOf('day');

                return currentDate.isSameOrAfter(dateFrom) && currentDate.isSameOrBefore(dateTo);
            });

            if (todaysHoliday) {
                const dateFrom = moment(todaysHoliday.dateFrom);
                const dateTo = moment(todaysHoliday.dateTo);

                this.$info({
                    title: this.$t('Information'),
                    content: (h: any) =>
                        h('div', [
                            h(
                                'p',
                                `${this.$t('ROLTEK will be closed due to collective leave from')} ${dateFrom.format(
                                    'DD.MM.YYYY',
                                )} ${this.$t('to')} ${dateTo.format('DD.MM.YYYY')}.`,
                            ),
                            h(
                                'p',
                                this.$t(
                                    'During this time, you are welcome to configure products, place orders, prepare offers, or send us a message via ROLTEKApp.',
                                ),
                            ),
                            h(
                                'p',
                                this.$t(
                                    'All received requests will be carefully processed immediately upon our return.',
                                ),
                            ),
                            h(
                                'p',
                                this.$t(
                                    'Thank you for your understanding and you are welcome to use the application during our leave.',
                                ),
                            ),
                        ]),
                    okText: this.$t('OK') as string,
                });
            }
        } catch (error) {
            console.error('Error fetching work-free days:', error);
        }
    }

    private hasErrors(fieldsError: any) {
        return Object.keys(fieldsError).some((field) => fieldsError[field]);
    }

    private userNameError() {
        const { getFieldError } = this.form;

        return getFieldError('username') ? getFieldError('username')[0] : '';
    }

    private passwordError() {
        const { getFieldError } = this.form;

        return getFieldError('password') ? getFieldError('password')[0] : '';
    }

    private handleSubmit(event: Event) {
        event.preventDefault();

        this.form.validateFields(
            // @ts-ignore
            (err: Error, loginValues: LoginValues) => {
                if (!err) {
                    this.login(loginValues);
                }
            },
        );
    }
}
